import { GitBranch, Pencil } from 'lucide-react';
import {
  BRANCH_API_STATE_LABELS,
  type Branch,
  type OperationalBranch,
} from '@quds/shared';
import { CountryDisplay } from '@/components/location';
import { Button, DescriptionList, Modal, StatusBadge } from '@/components/ui';
import { BRANCH_TYPE_LABELS } from '../components/labels';

function syncLabel(sync: OperationalBranch['sync']): string {
  if (sync === 'never_synced') return 'Never Synced';
  if (sync === 'unknown') return '—';
  return sync;
}

function syncTone(sync: OperationalBranch['sync']): 'success' | 'warning' | 'neutral' | 'danger' {
  if (sync === 'ONLINE') return 'success';
  if (sync === 'STALE') return 'warning';
  if (sync === 'OFFLINE') return 'danger';
  return 'neutral';
}

function apiTone(state: OperationalBranch['apiState']): 'success' | 'warning' | 'neutral' {
  if (state === 'configured' || state === 'configured_recoverable') return 'success';
  if (state === 'configured_not_recoverable') return 'warning';
  return 'neutral';
}

/** Read-only branch details; Edit opens BranchFormModal. */
export function BranchDetailsModal({
  branch,
  operational,
  canEdit,
  onEdit,
  onClose,
}: {
  branch: Branch | null;
  operational: OperationalBranch;
  canEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
}) {
  const branchId = operational.productionBranchRef ?? operational.caReferenceCode ?? branch?.referenceCode ?? '—';

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title="Branch Details"
      description={operational.name}
      icon={<GitBranch size={18} aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {canEdit && (
            <Button leftIcon={<Pencil size={14} aria-hidden="true" />} onClick={onEdit}>
              Edit Branch
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          <StatusBadge
            tone={operational.cloudPos === 'connected' ? 'success' : 'neutral'}
            label={operational.cloudPos === 'connected' ? 'Cloud POS Connected' : 'Cloud POS Not Connected'}
            dot={false}
          />
          <StatusBadge tone={apiTone(operational.apiState)} label={`API ${BRANCH_API_STATE_LABELS[operational.apiState]}`} dot={false} />
          {operational.cloudPos === 'connected' && (
            <StatusBadge tone={syncTone(operational.sync)} label={`Sync ${syncLabel(operational.sync)}`} />
          )}
          {operational.cloudPos === 'connected' && (
            <StatusBadge
              tone={operational.posDeviceCount > 0 ? 'success' : 'neutral'}
              label={operational.posDeviceCount > 0 ? 'POS Connected' : 'POS Not Connected'}
              dot={false}
            />
          )}
        </div>

        <DescriptionList
          columns={2}
          items={[
            { label: 'Branch Name', value: operational.name },
            { label: 'Branch ID', value: <span className="font-mono">{branchId}</span> },
            {
              label: 'Branch Type',
              value: branch ? BRANCH_TYPE_LABELS[branch.type] : '—',
            },
            {
              label: 'Country / City',
              value: branch ? (
                <CountryDisplay country={branch.country} city={branch.city} flagSize={14} />
              ) : (
                operational.locationLabel ?? '—'
              ),
            },
            {
              label: 'Governorate',
              value: branch?.administrativeAreaLevel1?.trim() || '—',
            },
            { label: 'Area', value: branch?.area?.trim() || '—' },
            { label: 'Address', value: branch?.address?.trim() || '—' },
            {
              label: 'Status',
              value: branch
                ? branch.status === 'active'
                  ? 'Active'
                  : 'Inactive'
                : operational.isActive
                  ? 'Active'
                  : 'Inactive',
            },
          ]}
        />
      </div>
    </Modal>
  );
}
